// ═══════════════════════════════════════════════════════════════════════════════
// LOGO PREVIEW — visual mockups for new tenant brands
//
// Renders proposed logos inline so a super admin can eyeball them at
// every common size before we commit actual /public/.svg files.
// Each design is a single SVG that uses a `color` prop so the same
// markup serves both light and dark variants.
//
// Current brands + variants:
//   ZaraTravels
//     • Z + Chevron — bold Z whose bottom stroke extends into an
//                      arrow tip.
//
//   TravelWithMe (four variants to choose from)
//     • Heart Cap     — two inward chevrons crowned with a heart curve
//     • Linked Pins   — two map pins side-by-side with a connecting arc
//     • Heart + Plane — heart outline with a small paper plane crossing
//     • Hand-in-Hand  — a stylized W where the strokes round into hands
// ═══════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { Copy, Check, Palette } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// ZaraTravels — Z + Chevron
// ─────────────────────────────────────────────────────────────────────────────
function ZaraTravelsMark({ size = 64, color = '#000' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
      <circle cx="32" cy="32" r="29" fill="none" stroke={color} strokeWidth="2" />
      <path d="M 20 22 L 44 22" stroke={color} strokeWidth="3" strokeLinecap="round" fill="none" />
      <path d="M 44 22 L 20 42" stroke={color} strokeWidth="3" strokeLinecap="round" fill="none" />
      <path
        d="M 20 42 L 44 42 L 40 38 M 44 42 L 40 46"
        stroke={color}
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TravelWithMe — variant A: Heart Cap (two chevrons + heart top)
// ─────────────────────────────────────────────────────────────────────────────
function TwmHeartCapMark({ size = 64, color = '#000' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
      <circle cx="32" cy="32" r="29" fill="none" stroke={color} strokeWidth="2" />
      <path
        d="M 20 28 L 32 40 L 32 50"
        stroke={color}
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d="M 44 28 L 32 40"
        stroke={color}
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d="M 22 24 a 4 4 0 0 1 5 -3 a 4 4 0 0 1 5 3 a 4 4 0 0 1 5 -3 a 4 4 0 0 1 5 3"
        stroke={color}
        strokeWidth="2.4"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TravelWithMe — variant B: Linked Pins (two map pins + connecting arc)
// ─────────────────────────────────────────────────────────────────────────────
function TwmLinkedPinsMark({ size = 64, color = '#000' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
      <circle cx="32" cy="32" r="29" fill="none" stroke={color} strokeWidth="2" />
      {/* Left pin */}
      <path
        d="M 22 20 C 17 20 17 28 22 28 C 27 28 27 20 22 20 Z"
        fill="none"
        stroke={color}
        strokeWidth="2.2"
      />
      <path d="M 22 28 L 22 36" stroke={color} strokeWidth="2.2" strokeLinecap="round" />
      <circle cx="22" cy="24" r="1.6" fill={color} />
      {/* Right pin */}
      <path
        d="M 42 20 C 37 20 37 28 42 28 C 47 28 47 20 42 20 Z"
        fill="none"
        stroke={color}
        strokeWidth="2.2"
      />
      <path d="M 42 28 L 42 36" stroke={color} strokeWidth="2.2" strokeLinecap="round" />
      <circle cx="42" cy="24" r="1.6" fill={color} />
      {/* Connecting arc */}
      <path
        d="M 22 40 Q 32 50 42 40"
        stroke={color}
        strokeWidth="2.4"
        strokeLinecap="round"
        fill="none"
        strokeDasharray="2.5 2.5"
      />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TravelWithMe — variant C: Heart + Plane
// ─────────────────────────────────────────────────────────────────────────────
function TwmHeartPlaneMark({ size = 64, color = '#000' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
      <circle cx="32" cy="32" r="29" fill="none" stroke={color} strokeWidth="2" />
      {/* Heart */}
      <path
        d="M 32 46 L 18 32 C 15 28 15 23 19 21 C 23 19 28 21 32 26 C 36 21 41 19 45 21 C 49 23 49 28 46 32 Z"
        fill="none"
        stroke={color}
        strokeWidth="2.4"
        strokeLinejoin="round"
      />
      {/* Small paper plane / arrow crossing the heart's center */}
      <path
        d="M 23 34 L 45 28 L 30 36 L 32 32 Z"
        fill={color}
        stroke={color}
        strokeWidth="0.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TravelWithMe — variant D: Hand-in-Hand "W"
// ─────────────────────────────────────────────────────────────────────────────
function TwmHandInHandMark({ size = 64, color = '#000' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
      <circle cx="32" cy="32" r="29" fill="none" stroke={color} strokeWidth="2" />
      {/* Left half of the W — two strokes that round into a "hand" */}
      <path
        d="M 18 22 L 24 42 L 32 30"
        stroke={color}
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      {/* Right half */}
      <path
        d="M 32 30 L 40 42 L 46 22"
        stroke={color}
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      {/* Two small dots at the top of each outer stroke — "heads" of the figures */}
      <circle cx="18" cy="22" r="2.4" fill={color} />
      <circle cx="46" cy="22" r="2.4" fill={color} />
      {/* Linking dot at the middle peak — "hands meeting" */}
      <circle cx="32" cy="30" r="2.4" fill={color} />
    </svg>
  );
}

// ── Raw SVG source builders (for the Copy-SVG buttons) ───────────────────────

function zaraTravelsSvg(stroke: '#000000' | '#ffffff'): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="256" height="256">
  <circle cx="32" cy="32" r="29" fill="none" stroke="${stroke}" stroke-width="2"/>
  <path d="M 20 22 L 44 22" stroke="${stroke}" stroke-width="3" stroke-linecap="round" fill="none"/>
  <path d="M 44 22 L 20 42" stroke="${stroke}" stroke-width="3" stroke-linecap="round" fill="none"/>
  <path d="M 20 42 L 44 42 L 40 38 M 44 42 L 40 46" stroke="${stroke}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
</svg>`;
}

function twmHeartCapSvg(stroke: '#000000' | '#ffffff'): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="256" height="256">
  <circle cx="32" cy="32" r="29" fill="none" stroke="${stroke}" stroke-width="2"/>
  <path d="M 20 28 L 32 40 L 32 50" stroke="${stroke}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
  <path d="M 44 28 L 32 40" stroke="${stroke}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
  <path d="M 22 24 a 4 4 0 0 1 5 -3 a 4 4 0 0 1 5 3 a 4 4 0 0 1 5 -3 a 4 4 0 0 1 5 3" stroke="${stroke}" stroke-width="2.4" stroke-linecap="round" fill="none"/>
</svg>`;
}

function twmLinkedPinsSvg(stroke: '#000000' | '#ffffff'): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="256" height="256">
  <circle cx="32" cy="32" r="29" fill="none" stroke="${stroke}" stroke-width="2"/>
  <path d="M 22 20 C 17 20 17 28 22 28 C 27 28 27 20 22 20 Z" fill="none" stroke="${stroke}" stroke-width="2.2"/>
  <path d="M 22 28 L 22 36" stroke="${stroke}" stroke-width="2.2" stroke-linecap="round"/>
  <circle cx="22" cy="24" r="1.6" fill="${stroke}"/>
  <path d="M 42 20 C 37 20 37 28 42 28 C 47 28 47 20 42 20 Z" fill="none" stroke="${stroke}" stroke-width="2.2"/>
  <path d="M 42 28 L 42 36" stroke="${stroke}" stroke-width="2.2" stroke-linecap="round"/>
  <circle cx="42" cy="24" r="1.6" fill="${stroke}"/>
  <path d="M 22 40 Q 32 50 42 40" stroke="${stroke}" stroke-width="2.4" stroke-linecap="round" fill="none" stroke-dasharray="2.5 2.5"/>
</svg>`;
}

function twmHeartPlaneSvg(stroke: '#000000' | '#ffffff'): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="256" height="256">
  <circle cx="32" cy="32" r="29" fill="none" stroke="${stroke}" stroke-width="2"/>
  <path d="M 32 46 L 18 32 C 15 28 15 23 19 21 C 23 19 28 21 32 26 C 36 21 41 19 45 21 C 49 23 49 28 46 32 Z" fill="none" stroke="${stroke}" stroke-width="2.4" stroke-linejoin="round"/>
  <path d="M 23 34 L 45 28 L 30 36 L 32 32 Z" fill="${stroke}" stroke="${stroke}" stroke-width="0.8" stroke-linejoin="round"/>
</svg>`;
}

function twmHandInHandSvg(stroke: '#000000' | '#ffffff'): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="256" height="256">
  <circle cx="32" cy="32" r="29" fill="none" stroke="${stroke}" stroke-width="2"/>
  <path d="M 18 22 L 24 42 L 32 30" stroke="${stroke}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
  <path d="M 32 30 L 40 42 L 46 22" stroke="${stroke}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
  <circle cx="18" cy="22" r="2.4" fill="${stroke}"/>
  <circle cx="46" cy="22" r="2.4" fill="${stroke}"/>
  <circle cx="32" cy="30" r="2.4" fill="${stroke}"/>
</svg>`;
}

// ── Reusable tile / copy block ───────────────────────────────────────────────

type MarkComponent = (props: { size?: number; color?: string }) => JSX.Element;

function MarkTile({ label, Mark, size }: { label: string; Mark: MarkComponent; size: number }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="flex items-center gap-3">
        <div className="rounded-lg bg-white p-3 border border-surface-200" style={{ width: size + 24, height: size + 24 }}>
          <Mark size={size} color="#0f172a" />
        </div>
        <div className="rounded-lg bg-slate-900 p-3" style={{ width: size + 24, height: size + 24 }}>
          <Mark size={size} color="#ffffff" />
        </div>
      </div>
      <span className="text-[11px] text-surface-500 font-mono">{label}</span>
    </div>
  );
}

function CopyBlock({ label, source }: { label: string; source: string }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(source);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {/* noop */}
  };
  return (
    <div className="mt-2 rounded-lg border border-surface-200 overflow-hidden">
      <div className="flex items-center justify-between bg-surface-50 px-3 py-2 border-b border-surface-200">
        <span className="text-[11px] font-mono text-surface-600">{label}</span>
        <button
          onClick={onCopy}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-surface-100 hover:bg-surface-200 text-surface-700"
        >
          {copied ? <Check size={11} /> : <Copy size={11} />}
          {copied ? 'Copied' : 'Copy SVG'}
        </button>
      </div>
      <pre className="px-3 py-2 text-[10px] font-mono text-surface-700 overflow-x-auto bg-surface-0 max-h-32">
{source}
      </pre>
    </div>
  );
}

// ── Variant card — one mark, several sizes, in-context mockups, copy blocks ──

interface VariantConfig {
  variantName: string;
  tagline:     string;
  Mark:        MarkComponent;
  brandName:   string;
  filenameBase: string;
  svgLight:    string;
  svgDark:     string;
}

function VariantCard({ v }: { v: VariantConfig }) {
  return (
    <div className="glass-panel p-5">
      <div className="mb-4">
        <h3 className="text-base font-bold text-surface-900">{v.variantName}</h3>
        <p className="text-xs text-surface-500 mt-0.5">{v.tagline}</p>
      </div>

      <div className="flex flex-wrap items-end gap-5">
        <MarkTile label="16 px"   Mark={v.Mark} size={16} />
        <MarkTile label="32 px"   Mark={v.Mark} size={32} />
        <MarkTile label="48 px"   Mark={v.Mark} size={48} />
        <MarkTile label="64 px"   Mark={v.Mark} size={64} />
        <MarkTile label="192 px"  Mark={v.Mark} size={192} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-5">
        {/* Browser-tab mockup */}
        <div className="rounded-lg border border-surface-200 overflow-hidden bg-surface-100">
          <div className="flex items-center gap-2 px-3 py-2 bg-surface-200 border-b border-surface-300">
            <span className="w-2 h-2 rounded-full bg-red-400" />
            <span className="w-2 h-2 rounded-full bg-amber-400" />
            <span className="w-2 h-2 rounded-full bg-emerald-400" />
            <div className="ml-2 inline-flex items-center gap-1 px-2 py-1 rounded bg-white border border-surface-300 text-xs">
              <v.Mark size={14} color="#0f172a" />
              <span className="text-surface-600">{v.brandName.toLowerCase()}.com</span>
            </div>
          </div>
          <div className="px-4 py-5">
            <div className="inline-flex items-center gap-2">
              <v.Mark size={28} color="#0f172a" />
              <span className="font-bold text-surface-900">{v.brandName}</span>
            </div>
            <p className="mt-2 text-[11px] text-surface-500">Header bar, light theme</p>
          </div>
        </div>
        {/* Splash hero mockup */}
        <div className="rounded-lg border border-slate-700 overflow-hidden bg-slate-900">
          <div className="px-4 py-8 flex flex-col items-center">
            <v.Mark size={64} color="#ffffff" />
            <span className="mt-3 font-bold text-white text-lg">{v.brandName}</span>
            <p className="mt-1 text-[11px] text-slate-400">Splash, dark theme</p>
          </div>
        </div>
      </div>

      <CopyBlock label={`${v.filenameBase}.svg (light)`}      source={v.svgLight} />
      <CopyBlock label={`${v.filenameBase}-dark.svg (dark)`}  source={v.svgDark} />
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function LogoPreviewPage() {
  // ZaraTravels — single variant
  const zaraTravels: VariantConfig[] = [
    {
      variantName: 'Z + Chevron',
      tagline:     'Bold Z whose bottom stroke extends into a chevron arrow — monogram + travel motion.',
      Mark:        ZaraTravelsMark,
      brandName:   'ZaraTravels',
      filenameBase: 'logo-zaratravels',
      svgLight:    zaraTravelsSvg('#000000'),
      svgDark:     zaraTravelsSvg('#ffffff'),
    },
  ];

  // TravelWithMe — four variants to choose from
  const travelWithMe: VariantConfig[] = [
    {
      variantName: 'Variant A — Heart Cap',
      tagline:     'Two inward chevrons crowned with a small heart curve. "Two travelers, one path."',
      Mark:        TwmHeartCapMark,
      brandName:   'TravelWithMe',
      filenameBase: 'logo-travelwithme-heartcap',
      svgLight:    twmHeartCapSvg('#000000'),
      svgDark:     twmHeartCapSvg('#ffffff'),
    },
    {
      variantName: 'Variant B — Linked Pins',
      tagline:     'Two map pins side-by-side, joined by a dotted arc. Reads as "you and me, on the map."',
      Mark:        TwmLinkedPinsMark,
      brandName:   'TravelWithMe',
      filenameBase: 'logo-travelwithme-linkedpins',
      svgLight:    twmLinkedPinsSvg('#000000'),
      svgDark:     twmLinkedPinsSvg('#ffffff'),
    },
    {
      variantName: 'Variant C — Heart + Plane',
      tagline:     'Heart outline pierced by a small paper plane. Romantic + travel-forward.',
      Mark:        TwmHeartPlaneMark,
      brandName:   'TravelWithMe',
      filenameBase: 'logo-travelwithme-heartplane',
      svgLight:    twmHeartPlaneSvg('#000000'),
      svgDark:     twmHeartPlaneSvg('#ffffff'),
    },
    {
      variantName: 'Variant D — Hand-in-Hand W',
      tagline:     'Stylized W with three dots — two heads at the top and a meeting point at the middle peak. Subtle "people-shaped W."',
      Mark:        TwmHandInHandMark,
      brandName:   'TravelWithMe',
      filenameBase: 'logo-travelwithme-handinhand',
      svgLight:    twmHandInHandSvg('#000000'),
      svgDark:     twmHandInHandSvg('#ffffff'),
    },
  ];

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="flex items-center gap-3 mb-2">
        <Palette size={24} className="text-brand-600" />
        <h1 className="text-2xl font-bold text-surface-900">Logo Preview</h1>
      </div>
      <p className="text-sm text-surface-500 mb-8">
        Proposed marks for new tenant brands. Each design is a single SVG so light + dark variants come for free.
        Tell me which variant(s) to ship and I'll commit the actual <code className="px-1 mx-1 bg-surface-100 rounded text-xs">/public/logo-…svg</code> files
        and wire them into the tenant's branding row.
      </p>

      {/* ── ZaraTravels ────────────────────────────────────────────── */}
      <section className="mb-12">
        <h2 className="text-xl font-bold text-surface-900 mb-1">ZaraTravels</h2>
        <p className="text-sm text-surface-500 mb-4">One proposed mark. Tell me to iterate if it's not landing.</p>
        <div className="space-y-6">
          {zaraTravels.map((v) => <VariantCard key={v.variantName} v={v} />)}
        </div>
      </section>

      {/* ── TravelWithMe ───────────────────────────────────────────── */}
      <section>
        <h2 className="text-xl font-bold text-surface-900 mb-1">TravelWithMe</h2>
        <p className="text-sm text-surface-500 mb-4">
          Four directions across the &ldquo;two travelers, together&rdquo; theme. Pick a favorite — or tell me to mix elements between them.
        </p>
        <div className="space-y-6">
          {travelWithMe.map((v) => <VariantCard key={v.variantName} v={v} />)}
        </div>
      </section>

      <div className="mt-8 p-4 rounded-lg bg-surface-50 border border-surface-200 text-xs text-surface-600">
        <p className="font-semibold mb-1">Next step</p>
        <p>
          Tell me which variant(s) to commit. I'll save the SVGs to <code className="px-1 bg-surface-100 rounded">/public</code>,
          set the favicon refs in <code className="px-1 bg-surface-100 rounded">index.html</code>, and (for tenant-scoped brands)
          upload to the storage bucket + set <code className="px-1 bg-surface-100 rounded">branding_configs.logo_url</code> /
          <code className="px-1 bg-surface-100 rounded">logo_dark_url</code>. We can also iterate before commit — different stroke
          weights, color fills, or new motifs.
        </p>
      </div>
    </div>
  );
}
